import React, { useEffect, useMemo, useState } from 'react';

import type { UnitType } from '@r6digital/rapidstor-sdk/domain';
import type Location from '@r6digital/rapidstor-types/react/entities/Location';

import classes from '@/standard-unit-listing/components/unit-type/styles.module.scss';

import { useListingSettings } from '../../sdk/useSite';

export interface UnitTypeImageGalleryProps {
    unitType: UnitType;
    location: Location;
}

/**
 * The unit type's primary image with a row of carousel dots for its additional slides.
 *
 * Both the picture and the slides come off `unitType.descriptor` — the back-reference is what lets a
 * card reach its merchandising without the screen carrying the association.
 */
export default function UnitTypeImageGallery({ unitType, location }: UnitTypeImageGalleryProps) {
    const settings = useListingSettings(location);
    const { descriptor } = unitType;

    const images = useMemo(() => {
        const list: string[] = [];
        const fallback = settings.defaultUnitPicture();
        if (descriptor?.picture) list.push(descriptor.picture);
        else if (fallback) list.push(fallback);

        for (const slide of descriptor?.slides ?? []) {
            if (slide && !list.includes(slide)) list.push(slide);
        }
        return list;
    }, [descriptor, settings]);

    const [activeIdx, setActiveIdx] = useState(0);

    // Reset to the first image when the active variant changes, so dot and image stay in sync.
    useEffect(() => {
        setActiveIdx(0);
    }, [unitType.id]);

    if (!images.length) return null;

    return (
        <div className={classes.imageGallery}>
            <div className={classes.imageWrapper}>
                <img
                    src={images[Math.min(activeIdx, images.length - 1)]}
                    alt={unitType.typeName}
                    className={classes.picture}
                />
                {images.length > 1 && (
                    <div className={classes.dots} role="list" aria-label="Image selector">
                        {images.map((src, idx) => (
                            <button
                                key={src + idx}
                                type="button"
                                aria-label={`Select image ${idx + 1}`}
                                aria-current={activeIdx === idx}
                                onClick={() => setActiveIdx(idx)}
                                className={`${classes.dot} ${
                                    activeIdx === idx ? classes.dotActive : ''
                                }`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
